import { FC, useCallback, useState } from 'react'
import { PropsWithChildren } from 'react'
import { useQuery } from 'react-query'

import { UsersAPI } from '@api/usersAPI'

import { AuthToken } from '@models/auth'
import { UserMeModel } from '@models/userMe'

import { SplashScreen } from '@modules/splash-screen/components/SplashScreen'

import { QUERIES } from '@/constants/queries'
import dayjs from '@/libs/dayjs'
import StorageUtil, { StorageKeyEnum } from '@/utils/StorageUtil'
import TypeUtil from '@/utils/typeUtil'

import { AuthContext } from '../contexts/AuthContext'

const AuthProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const [token, setTokenState] = useState<AuthToken | null>(StorageUtil.get(StorageKeyEnum.AUTH_TOKEN))

  const {
    refetch,
    data: currentUser,
    isLoading,
  } = useQuery({
    cacheTime: 0,
    retry: 0,
    enabled: TypeUtil.isDefined(token),
    queryKey: [QUERIES.USER_ME, token?.access_token],
    queryFn: () => UsersAPI.getMe().then((r) => r.data.me),
    select: (response) => (token ? response : undefined),
    onSuccess: (user: UserMeModel | undefined) => {
      if (user?.preferred_timezone) {
        dayjs.setDefault(user.preferred_timezone)
      }

      if (user?.active_clinic_id) {
        StorageUtil.set(StorageKeyEnum.ACTIVE_CLINIC_ID, user.active_clinic_id)
      } else {
        StorageUtil.remove(StorageKeyEnum.ACTIVE_CLINIC_ID)
      }

      if (user?.active_view_mode) {
        StorageUtil.set(StorageKeyEnum.ACTIVE_VIEW_MODE, user.active_view_mode)
      }
    },
    onError: () => removeToken(),
  })

  const setToken = useCallback((newToken: AuthToken) => {
    StorageUtil.set(StorageKeyEnum.AUTH_TOKEN, newToken)

    setTokenState(newToken)
  }, [])

  const removeToken = useCallback(async () => {
    StorageUtil.reset()
    window.location.reload()
  }, [])

  if (isLoading) {
    return <SplashScreen />
  }

  return (
    <AuthContext.Provider
      value={{
        token,
        setToken,
        currentUser,
        isLoading,
        removeToken,
        refetchAuth: refetch,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export { AuthProvider }
